import type {
  SocialPostSceneBlockV1,
  SocialPostScenePlanV1,
  SocialPostTemplateEditOperation,
} from "@runcheetah/shared";

import type { CreativeLayerCreateKind } from "./campaign-creative-layer-factory";

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

export function updateBlockInPlan(
  plan: SocialPostScenePlanV1,
  blockId: string,
  changes: Record<string, unknown>,
): SocialPostScenePlanV1 {
  return {
    ...plan,
    scene: {
      ...plan.scene,
      blocks: plan.scene.blocks.map((block) => updateBlock(block, blockId, changes)),
    },
  } as SocialPostScenePlanV1;
}

export function commitNumericValue(input: {
  value: string;
  fallback: number;
  min?: number;
  max?: number;
  onCommit: (value: number) => void;
  reset: (value: string) => void;
}) {
  const parsedValue = Number(input.value);

  if (!Number.isFinite(parsedValue)) {
    input.reset(String(input.fallback));
    return;
  }

  const clampedValue = Math.min(
    input.max ?? parsedValue,
    Math.max(input.min ?? parsedValue, parsedValue),
  );

  input.reset(String(clampedValue));
  input.onCommit(clampedValue);
}

export function normalizeColorValue(value: string) {
  if (HEX_COLOR_RE.test(value)) {
    return value;
  }

  return "#ffffff";
}

export function createBlockEditOperation(
  block: SocialPostSceneBlockV1,
  changes: Record<string, unknown>,
): SocialPostTemplateEditOperation | null {
  switch (block.type) {
    case "text":
      return {
        type: "update_text_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    case "image":
      return {
        type: "update_image_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    case "logo":
      return {
        type: "update_logo_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    case "rect":
      return {
        type: "update_rect_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    case "shape":
      return {
        type: "update_shape_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    case "frame":
      return {
        type: "update_frame_block",
        blockId: block.id,
        changes,
      } as SocialPostTemplateEditOperation;
    default:
      return null;
  }
}

export function createLayerId(kind: CreativeLayerCreateKind) {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);

  return `${kind}_${suffix}`;
}

export function canRemoveSelectedLayer(blocks: SocialPostSceneBlockV1[], blockId: string): boolean {
  if (blocks.length === 0) {
    return false;
  }

  if (blocks.some((block) => block.id === blockId)) {
    return blocks.length > 1;
  }

  return false;
}

export function canRenameLayer(block: SocialPostSceneBlockV1) {
  return ["image", "logo", "rect", "shape", "frame"].includes(block.type);
}

export function getCustomLayerName(block: SocialPostSceneBlockV1) {
  return "layerName" in block && typeof block.layerName === "string"
    ? block.layerName.trim() || null
    : null;
}

export function getLayerSwatchInitials(block: SocialPostSceneBlockV1) {
  switch (block.type) {
    case "text":
      return "TX";
    case "image":
      return "IM";
    case "logo":
      return "LO";
    case "frame":
      return "FR";
    case "rect":
    case "shape":
      return "SH";
    default:
      return "LY";
  }
}

export function getLayerSwatchColor(block: SocialPostSceneBlockV1) {
  if (block.type === "text" || block.type === "rect") {
    return block.fill;
  }

  if (block.type === "frame") {
    return block.fill ?? block.stroke;
  }

  if (block.type === "shape") {
    return block.fill ?? block.stroke;
  }

  return null;
}

export function truncateLayerLabel(value: string) {
  const compact = value.replace(/\s+/g, " ").trim();
  return compact.length > 36 ? `${compact.slice(0, 33)}...` : compact;
}

function updateBlock(
  block: SocialPostSceneBlockV1,
  blockId: string,
  changes: Record<string, unknown>,
): SocialPostSceneBlockV1 {
  if (block.id === blockId) {
    return { ...block, ...changes } as SocialPostSceneBlockV1;
  }

  return block;
}
